import { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Header } from "./components/Header";
import { StatsRow } from "./components/StatsRow";
import { LiveTagStream } from "./components/LiveTagStream";
import { TopTagsChart } from "./components/TopTagsChart";
import { AggregatesTable } from "./components/AggregatesTable";
import { useLiveStream } from "./hooks/useLiveStream";
import { api } from "./lib/api";
import "./index.css";

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchInterval: 5000, retry: 1 } },
});

function Dashboard() {
  const [tenant, setTenant] = useState<string | null>(null);

  const tenantsQ = useQuery({ queryKey: ["tenants"], queryFn: api.tenants });
  const statsQ = useQuery({ queryKey: ["stats"], queryFn: api.stats });

  const tenants = tenantsQ.data?.tenants ?? [];

  useEffect(() => {
    if (!tenant && tenants.length > 0) setTenant(tenants[0]!);
  }, [tenants, tenant]);

  const topTagsQ = useQuery({
    queryKey: ["top-tags", tenant],
    queryFn: () => api.topTags(tenant!),
    enabled: !!tenant,
  });
  const aggsQ = useQuery({
    queryKey: ["aggregates", tenant],
    queryFn: () => api.aggregates(tenant!),
    enabled: !!tenant,
  });

  const { events, connected } = useLiveStream(tenant);

  return (
    <div className="min-h-screen">
      <Header
        tenants={tenants}
        current={tenant}
        onChange={setTenant}
        connected={connected}
      />

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        <HeroBlurb />

        <StatsRow stats={statsQ.data ?? null} />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2">
            <LiveTagStream events={events} connected={connected} />
          </div>
          <div className="lg:col-span-3 space-y-6">
            <TopTagsChart tags={topTagsQ.data?.tags ?? []} />
            <AggregatesTable rows={aggsQ.data?.aggregates ?? []} />
          </div>
        </div>

        <Footer />
      </main>
    </div>
  );
}

function HeroBlurb() {
  return (
    <section className="rounded-lg border border-brand-900/40 bg-gradient-to-r from-brand-950/60 via-zinc-900/40 to-zinc-900/20 p-6">
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <h1 className="text-xl font-semibold">
            Live shelf telemetry —{" "}
            <span className="text-brand-400">why shoppers reject your product, in real time</span>
          </h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
            Every row below originated as a fleeting acoustic event at a physical
            shelf, processed entirely on-device. No raw audio left the edge. No
            transcripts were generated. No shopper was identified. What you see is
            <em> only </em>the bucketed, k-thresholded semantic counts the privacy
            engine chose to release.
          </p>
        </div>
        <div className="hidden md:block text-right text-[11px] text-zinc-500 leading-snug">
          <div>US Utility Application</div>
          <div className="font-mono text-zinc-300">#19/562,766</div>
          <div className="mt-1">Priority 2026-02-13</div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="pt-8 border-t border-zinc-800/60 text-[11px] text-zinc-500 flex flex-wrap items-center gap-4">
      <span>© 2026 ShelvesLab</span>
      <span className="opacity-50">·</span>
      <span>Privacy-by-architecture · zero-retention · planogram-locked</span>
      <a href="https://shelveslab.com" className="ml-auto text-brand-400 hover:text-brand-300">
        shelveslab.com →
      </a>
    </footer>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Dashboard />
    </QueryClientProvider>
  );
}
